/**
 * ResetPassword.js — Password-reset landing page.
 *
 * Reads the reset_url token from the query string (injected by Shopify's
 * password-reset email), validates matching passwords, then calls the
 * customerResetByUrl GraphQL mutation. Redirects to home if no reset_url
 * is present. Shows a login button on success.
 *
 * Props:
 *  - ShowLoginPopup {Function}  Opens the login modal (called after success).
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { Button, Modal, Container, Row, Col, Alert, Form } from "react-bootstrap";
import { useMutation } from "@apollo/react-hooks";
import { useLocation } from "react-router";
import queryString from "query-string";

// ─── GraphQL ──────────────────────────────────────────────────────────────────
import { customerResetByUrl } from "../grappql/Customers/Mutations/customerResetByUrl";

export const ResetPassword = (props) => {
  const location = useLocation();

  const queryParam = queryString.parse(location.search);

  if (queryParam["reset_url"] === undefined) {
    window.location.href = "/";
  }

  const ResetUrl =
    queryParam["reset_url"] !== undefined && queryParam["reset_url"] !== null
      ? queryParam["reset_url"]
      : undefined;

  const [user_password, set_user_password] = useState("");
  const [conf_password, set_conf_password] = useState("");

  const [ErrorMessage, setErrorMessage] = useState(undefined);
  const [SuccessMessage, setSuccessMessage] = useState(undefined);
  const [SomethingUpadating, setSomethingUpadating] = useState(false);
  const [password_ER, set_password_ER] = useState("");
  const [confirm_ER, set_confirm_ER] = useState("");

  const [ResetPassword, { data: recovered_data }] =
    useMutation(customerResetByUrl);

  /**
   * Form submit handler. Validates that both password fields match,
   * then fires the customerResetByUrl mutation with the reset URL token.
   * @param {React.SyntheticEvent} e - Form submit event.
   */
  const submit = (e) => {
    e.preventDefault();
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setSomethingUpadating(false);
    set_password_ER("");
    set_confirm_ER("");
    if (!user_password) {
      set_password_ER("Password is required");
    } else if (user_password !== conf_password) {
      set_confirm_ER("Both passwords must match");
    } else {
      setSomethingUpadating(true);
      ResetPassword({
        variables: {
          password: user_password,
          resetUrl: ResetUrl,
        },
      });
    }
  };

  /** Opens the login modal via the ShowLoginPopup prop. */
  const openLoginPopup = () => {
    props.ShowLoginPopup();
  };

  useEffect(() => {
    if (recovered_data !== undefined) {
      if (
        recovered_data.customerResetByUrl.customerUserErrors !== undefined &&
        recovered_data.customerResetByUrl.customerUserErrors.length
      ) {
        recovered_data.customerResetByUrl.customerUserErrors.forEach(function (
          error
        ) {
          setErrorMessage(error.message);
          setSomethingUpadating(false);
        });
      } else {
        setSomethingUpadating(false);
        setSuccessMessage("Your password has been updated successfully !");
      }
    }
  }, [recovered_data]);

  return (
    <Container>
      <br />
      <Row className="justify-content-center">
        <Col md={4} xs={12}>
          <Form className="" onSubmit={submit}>
            <Modal.Title className="text-center">Reset Password</Modal.Title>
            {SuccessMessage === undefined ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="password"
                    className={password_ER ? "form-error" : ""}
                    onChange={(e) => { set_user_password(e.target.value); if (password_ER) set_password_ER(""); }}
                    value={user_password}
                    placeholder="Enter your new password"
                  />
                  {password_ER && <span className="field-error-msg">{password_ER}</span>}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="password"
                    className={confirm_ER ? "form-error" : ""}
                    onChange={(e) => { set_conf_password(e.target.value); if (confirm_ER) set_confirm_ER(""); }}
                    value={conf_password}
                    placeholder="Confirm your password"
                  />
                  {confirm_ER && <span className="field-error-msg">{confirm_ER}</span>}
                </Form.Group>
              </>
            ) : (
              ""
            )}

            {ErrorMessage !== undefined ? (
              <Alert variant="danger">{ErrorMessage}</Alert>
            ) : (
              ""
            )}
            {SuccessMessage !== undefined ? (
              <>
                <Alert variant="success">{SuccessMessage}</Alert>
                <Button
                  className="w-100 rounded-2 btn-lg border-2"
                  onClick={(e) => openLoginPopup()}>
                  Login
                </Button>
              </>
            ) : (
              ""
            )}
            {SomethingUpadating === true ? (
              <Alert variant="primary">Validating...</Alert>
            ) : (
              ""
            )}
            {SuccessMessage === undefined ? (
              <Button
                variant="primary"
                type="submit"
                className="w-100 rounded-2 btn-lg border-2">
                Submit
              </Button>
            ) : (
              ""
            )}
          </Form>
        </Col>
      </Row>
    </Container>
  );
};
